import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Checkbox,
  DatePicker,
  Drawer,
  Field,
  FieldGroup,
  Input,
  FileField,
  Toggle
} from '@itds/elements/index.js';

import { Api } from '@brokerage/utils/Api';

const Ad = ({ ad, mobile_app, onClose, onSave, show }) => {
  const styles = {
    container: {
      border: '1px solid #e5e5e5',
      boxShadow: '0px 3px 5px rgb(0 0 0 / 10%)',
      borderRadius: 5,
      marginBottom: 30,
      padding: 20,
      display: 'flex',
      justifyContent: 'space-between'
    },
    banner_image_container: {
      paddingTop: '31.25%',
      backgroundColor: '#fff',
      position: 'relative',
      width: '100%',
      marginBottom: 20
    },
    banner_popup_ad_container: {
      paddingTop: '100%',
      backgroundColor: '#fff',
      position: 'relative',
      width: '100%',
      marginBottom: 20
    },
    img: {
      maxWidth: '100%',
      maxHeight: '100%',
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)'
    },
    img_placeholder: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      fontStyle: 'italic',
      color: '#ccc'
    },
    interstitial_image_container: {
      paddingTop: '132.8%',
      backgroundColor: '#fff',
      position: 'relative',
      width: '100%',
      marginBottom: 20
    },
    interstitial_ad: {
      maxWidth: '100%',
      maxHeight: '100%',
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)'
    }
  };

  const [data, setData] = useState({});
  const [errors, setErrors] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const new_ad = ad || {};

    const currentDate = new Date().toISOString();

    setData({
      is_active: new_ad.is_active,
      id: new_ad.id,
      name: new_ad.name || '',
      start_date: new_ad.start_date || currentDate,
      end_date: new_ad.end_date || currentDate,
      banner_ad_click_count: new_ad.banner_ad_click_count || 0,
      banner_ad_image: null,
      banner_ad_popup_click_count: new_ad.banner_ad_popup_click_count || 0,
      banner_ad_popup_image: null,
      banner_ad_url: new_ad.banner_ad_url || null,
      interstitial_ad_click_count: new_ad.interstitial_ad_click_count || 0,
      interstitial_ad_click_to_continue: new_ad.interstitial_ad_click_to_continue,
      interstitial_ad_duration: new_ad.interstitial_ad_duration || 0,
      interstitial_ad_image: null,
      interstitial_ad_url: new_ad.interstitial_ad_url || null,
      mobile_app_guid: mobile_app.guid
    });
  }, [ad, mobile_app]);

  const handleInputChange = (name, e) => {
    setData({ ...data, [name]: e.target.value });
  };

  const handleFileChange = (name, file) => {
    setData({ ...data, [name]: file });
  };

  const handleCheck = (name, e) => {
    setData({ ...data, [name]: e.target.checked });
  };

  const handleActiveChange = () => {
    setData({ ...data, is_active: !data.is_active });
  };

  const handleDateChange = (name, date) => {
    setData({ ...data, [name]: date });
  };

  const handleSave = () => {
    const errors = [];
    const required_fields = ['name', 'start_date', 'end_date'];

    required_fields.forEach(field => {
      if (!data[field]) {
        errors.push(field);
      }
    });

    if (data.start_date > data.end_date) {
      errors.push('start_date', 'end_date');
    }

    if (data.end_date < data.start_date) {
      errors.push('start_date', 'end_date');
    }

    setErrors(errors);

    if (!errors.length) {
      const { id, ...payload } = data;

      setSaving(true);

      Api.updateAgencyMobileAppAdCampaign(id, payload)
        .then(() => {
          onSave('success');
          setSaving(false);
        })
        .catch(() => {
          onSave('error');
          setSaving(false);
        });
    }
  };

  return (
    <Drawer
      alignRight={true}
      footer={
        saving ? (
          <Button
            disabled={saving}
            icon={saving ? 'loading mdi-spin' : null}
            inline={true}
            onClick={handleSave}
          >
            Saving...
          </Button>
        ) : (
          <Button onClick={handleSave}>{data.id ? 'Save Changes' : 'Create New Ad'}</Button>
        )
      }
      onClose={onClose}
      show={show}
      title={data.id ? 'Editing Ad' : 'New Mobile App Ad'}
    >
      <Field>
        <Toggle active={data.is_active} onClick={handleActiveChange} />
      </Field>

      <Field label='Ad Name'>
        <Input
          invalid={errors.includes('name')}
          onChange={handleInputChange.bind(null, 'name')}
          value={data.name}
        />
      </Field>

      <Field label='Start Date'>
        <DatePicker
          future={true}
          invalid={errors.includes('start_date')}
          onChange={handleDateChange.bind(null, 'start_date')}
          value={data.start_date}
        />
      </Field>

      <Field label='End Date'>
        <DatePicker
          future={true}
          invalid={errors.includes('end_date')}
          onChange={handleDateChange.bind(null, 'end_date')}
          value={data.end_date}
        />
      </Field>

      <FieldGroup contained={true} label='Interstitial Ad'>
        <div style={styles.interstitial_image_container}>
          {ad && ad.interstitial_ad_image && ad.interstitial_ad_image.url ? (
            <img alt='interstitial ad' src={ad.interstitial_ad_image.url} style={styles.img} />
          ) : (
            <div style={styles.img_placeholder}>No Image</div>
          )}
        </div>

        <Field label='Image'>
          <FileField
            file={data.interstitial_ad_image}
            fileUrl={ad && ad.interstitial_ad_image ? ad.interstitial_ad_image.url : null}
            onChange={handleFileChange.bind(null, 'interstitial_ad_image')}
          />
        </Field>

        <Field label='Duration (is Milliseconds)'>
          <Input
            onChange={handleInputChange.bind(null, 'interstitial_ad_duration')}
            step={1}
            type='number'
            value={data.interstitial_ad_duration}
          />
        </Field>

        <Field label='URL'>
          <Input
            onChange={handleInputChange.bind(null, 'interstitial_ad_url')}
            value={data.interstitial_ad_url}
          />
        </Field>

        <Checkbox
          checked={data.interstitial_ad_click_to_continue}
          label='Click to Continue'
          onChange={handleCheck.bind(null, 'interstitial_ad_click_to_continue')}
        />
      </FieldGroup>

      <FieldGroup contained={true} label='Banner/PopUp Ad'>
        <div style={styles.banner_image_container}>
          {ad && ad.banner_ad_image && ad.banner_ad_image.url !== null ? (
            <img alt='banner ad' src={ad.banner_ad_image.url} style={styles.img} />
          ) : (
            <div style={styles.img_placeholder}>No Image</div>
          )}
        </div>

        <Field label='Banner Image'>
          <FileField
            file={data.banner_ad_image}
            fileUrl={ad && ad.banner_ad_image ? ad.banner_ad_image.url : null}
            onChange={handleFileChange.bind(null, 'banner_ad_image')}
          />
        </Field>

        <div style={styles.banner_popup_ad_container}>
          {ad && ad.banner_ad_popup_image && ad.banner_ad_popup_image.url ? (
            <img alt='banner ad popup' src={ad.banner_ad_popup_image.url} style={styles.img} />
          ) : (
            <div style={styles.img_placeholder}>No Image</div>
          )}
        </div>

        <Field label='PopUp Image'>
          <FileField
            file={data.banner_ad_popup_image}
            fileUrl={ad && ad.banner_ad_popup_image ? ad.banner_ad_popup_image.url : null}
            onChange={handleFileChange.bind(null, 'banner_ad_popup_image')}
          />
        </Field>

        <Field label='URL'>
          <Input
            onChange={handleInputChange.bind(null, 'banner_ad_url')}
            value={data.banner_ad_url}
          />
        </Field>
      </FieldGroup>
    </Drawer>
  );
};

Ad.propTypes = {
  ad: PropTypes.object,
  mobile_app: PropTypes.object,
  onClose: PropTypes.func,
  onSave: PropTypes.func,
  show: PropTypes.bool
};

export default Ad;
